import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { authApi } from '@/services/api';
import { LoadingSpinner } from '@/components/common';

const MIN_PASSWORD_LENGTH = 8;

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenMissing, setTokenMissing] = useState(false);

  useEffect(() => {
    if (!token) setTokenMissing(true);
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (password.length < MIN_PASSWORD_LENGTH) {
      toast.error(`La contraseña debe tener al menos ${MIN_PASSWORD_LENGTH} caracteres`);
      return;
    }
    if (password !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      toast.success('Contraseña actualizada');
      setTimeout(() => navigate('/login'), 2500);
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        'No se pudo restablecer la contraseña. El enlace puede haber caducado.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-vida-900 to-gray-900 px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-vida-500 rounded-2xl mb-4">
            <span className="text-white font-bold text-3xl">V</span>
          </div>
          <h1 className="text-2xl font-bold text-white">VIDA System</h1>
          <p className="text-gray-400 mt-2">Restablecer contraseña</p>
        </div>

        <div className="bg-white rounded-2xl shadow-xl p-8">
          {tokenMissing ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-yellow-100 rounded-full">
                <ExclamationTriangleIcon className="w-7 h-7 text-yellow-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Enlace inválido</h2>
              <p className="text-sm text-gray-600">
                El enlace de recuperación no es correcto o está incompleto. Vuelve a
                solicitar un nuevo enlace desde el formulario de "He olvidado mi
                contraseña".
              </p>
              <Link
                to="/forgot-password"
                className="inline-flex items-center gap-1 text-sm text-vida-600 hover:text-vida-700 font-medium"
              >
                Solicitar nuevo enlace
              </Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-4">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-green-100 rounded-full">
                <CheckCircleIcon className="w-7 h-7 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">
                Contraseña actualizada
              </h2>
              <p className="text-sm text-gray-600">
                Tu contraseña se ha restablecido correctamente. Te redirigimos al
                inicio de sesión...
              </p>
            </div>
          ) : (
            <>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Crea una nueva contraseña
              </h2>
              <p className="text-sm text-gray-500 mb-6">
                Mínimo {MIN_PASSWORD_LENGTH} caracteres. Si tienes 2FA activado, lo
                seguirás necesitando para iniciar sesión.
              </p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label htmlFor="password" className="label">
                    Nueva contraseña
                  </label>
                  <input
                    id="password"
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="label">
                    Confirmar contraseña
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    className="input"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    autoComplete="new-password"
                    minLength={MIN_PASSWORD_LENGTH}
                  />
                </div>

                <button
                  type="submit"
                  className="btn-primary w-full py-3"
                  disabled={loading || !password || !confirmPassword}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <LoadingSpinner size="sm" />
                      Restableciendo...
                    </span>
                  ) : (
                    'Restablecer contraseña'
                  )}
                </button>

                <div className="text-center">
                  <Link
                    to="/login"
                    className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                  >
                    <ArrowLeftIcon className="w-4 h-4" />
                    Volver a iniciar sesión
                  </Link>
                </div>
              </form>
            </>
          )}
        </div>

        <p className="mt-8 text-center text-sm text-gray-500">
          VIDA System &copy; {new Date().getFullYear()}
        </p>
      </div>
    </div>
  );
}
